import { useEffect, useState, useCallback } from "react";
import { Database, DownloadCloud, History, Trash2, AlertTriangle, Clock, FileDown } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { Modal } from "./shared";

export function BackupsPanel() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmRestore, setConfirmRestore] = useState(null); // backup obj
  const [confirmDelete, setConfirmDelete] = useState(null);   // backup obj

  const load = useCallback(() => api.get("/admin/backups").then((r) => setData(r.data)).catch(() => setData({ backups: [], max_backups: 5 })), []);
  useEffect(() => { load(); }, [load]);

  const createNow = async () => {
    setBusy(true);
    try { await api.post("/admin/backups"); toast.success("Backup created"); await load(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const doRestore = async () => {
    setBusy(true);
    try { const r = await api.post(`/admin/backups/${confirmRestore.id}/restore`); toast.success(`Restored ${r.data.restored_docs} records`); setConfirmRestore(null); await load(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const doDelete = async () => {
    setBusy(true);
    try { await api.delete(`/admin/backups/${confirmDelete.id}`); toast.success("Backup deleted"); setConfirmDelete(null); await load(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const doDownload = async (b) => {
    setBusy(true);
    try {
      const r = await api.get(`/admin/backups/${b.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/json" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `lanista-backup-${new Date(b.created_at).toISOString().slice(0, 10)}-${b.id}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Backup downloaded");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const backups = data?.backups || [];
  const lastAuto = backups.find((b) => b.trigger === "auto" && b.status === "complete");

  return (
    <div data-testid="backups-panel" className="mb-6 border border-sky-900/50 bg-gradient-to-br from-sky-950/20 to-zinc-950 p-4 rounded-sm">
      <div className="flex items-center justify-between gap-3 mb-1 flex-wrap">
        <div className="flex items-center gap-2">
          <Database size={16} className="text-sky-400" />
          <h3 className="font-cinzel text-sky-300 uppercase tracking-widest text-sm">Database Backups</h3>
        </div>
        <button data-testid="create-backup-btn" onClick={createNow} disabled={busy}
          className="inline-flex items-center gap-2 px-4 py-2 bg-sky-600 text-white font-cinzel uppercase tracking-wide text-sm hover:bg-sky-500 rounded-sm disabled:opacity-50">
          <DownloadCloud size={15} /> {busy ? "Working…" : "Create Backup Now"}
        </button>
      </div>
      <div data-testid="last-auto-backup" className="flex items-center gap-1.5 text-[11px] text-sky-300/80 mb-3">
        <Clock size={12} />
        {lastAuto
          ? <>Last automatic backup: <span className="text-sky-200 tabular-nums">{new Date(lastAuto.completed_at || lastAuto.created_at).toLocaleString()}</span></>
          : <span className="text-zinc-500">No automatic backup has run yet.</span>}
      </div>
      <p className="text-sm text-zinc-500 mb-4">
        Automatic save-states run <span className="text-sky-300">Sundays &amp; Thursdays at 02:00 (game time)</span>. The {data?.max_backups || 5} most recent are kept — older ones are removed automatically. Restoring fully replaces the current game data with that snapshot. Use <span className="text-emerald-300">Download</span> to save an off-database JSON copy for safekeeping.
      </p>

      {data === null ? (
        <div className="text-sm text-zinc-600">Loading backups…</div>
      ) : backups.length === 0 ? (
        <div data-testid="backups-empty" className="text-sm text-zinc-600 border border-zinc-800 bg-zinc-950/60 px-4 py-6 text-center rounded-sm">No backups yet. Create one now or wait for the next scheduled save-state.</div>
      ) : (
        <div className="space-y-2">
          {backups.map((b) => (
            <div key={b.id} data-testid={`backup-row-${b.id}`} className="flex items-center justify-between gap-3 flex-wrap border border-zinc-800 bg-zinc-950/60 px-4 py-3 rounded-sm">
              <div className="min-w-[200px]">
                <div className="flex items-center gap-2">
                  <span className="text-zinc-200 text-sm font-medium tabular-nums">{new Date(b.created_at).toLocaleString()}</span>
                  <span className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm ${b.trigger === "auto" ? "bg-sky-950/60 text-sky-300 border border-sky-800/50" : "bg-amber-950/40 text-amber-300 border border-amber-800/50"}`}>{b.trigger}</span>
                  {b.status !== "complete" && <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm bg-red-950/50 text-red-300 border border-red-800/50">{b.status}</span>}
                </div>
                <div className="text-[11px] text-zinc-500 mt-0.5 tabular-nums">{b.total_docs} records · {Object.keys(b.collections || {}).length} collections</div>
              </div>
              <div className="flex items-center gap-2">
                <button data-testid={`restore-backup-${b.id}`} onClick={() => setConfirmRestore(b)} disabled={busy || b.status !== "complete"}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs border border-sky-800/60 text-sky-300 hover:border-sky-500 hover:text-sky-200 rounded-sm transition-colors disabled:opacity-40">
                  <History size={13} /> Restore
                </button>
                <button data-testid={`download-backup-${b.id}`} onClick={() => doDownload(b)} disabled={busy || b.status !== "complete"}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs border border-emerald-800/60 text-emerald-300 hover:border-emerald-500 hover:text-emerald-200 rounded-sm transition-colors disabled:opacity-40">
                  <FileDown size={13} /> Download
                </button>
                <button data-testid={`delete-backup-${b.id}`} onClick={() => setConfirmDelete(b)} disabled={busy}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs border border-zinc-700 text-zinc-400 hover:border-red-600 hover:text-red-300 rounded-sm transition-colors disabled:opacity-40">
                  <Trash2 size={13} /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {confirmRestore && (
        <Modal title="Restore Backup" testid="restore-backup-confirm" onClose={() => setConfirmRestore(null)}>
          <div className="flex items-start gap-2 text-sm text-zinc-300">
            <AlertTriangle size={18} className="text-red-400 shrink-0 mt-0.5" />
            <p>This will <span className="text-red-300 font-semibold">permanently replace ALL current game data</span> with the snapshot from <span className="text-sky-300">{new Date(confirmRestore.created_at).toLocaleString()}</span> ({confirmRestore.total_docs} records). Any changes since then will be lost. This cannot be undone.</p>
          </div>
          <div className="mt-5 flex gap-2 justify-end">
            <button data-testid="restore-cancel" onClick={() => setConfirmRestore(null)} disabled={busy} className="px-4 py-2 text-sm border border-zinc-700 text-zinc-300 hover:border-zinc-500 rounded-sm">Cancel</button>
            <button data-testid="restore-confirm-btn" onClick={doRestore} disabled={busy} className="px-4 py-2 text-sm font-cinzel uppercase tracking-wide bg-red-600 text-white hover:bg-red-500 rounded-sm disabled:opacity-50">{busy ? "Restoring…" : "Replace & Restore"}</button>
          </div>
        </Modal>
      )}

      {confirmDelete && (
        <Modal title="Delete Backup" testid="delete-backup-confirm" onClose={() => setConfirmDelete(null)}>
          <p className="text-sm text-zinc-300">Delete the backup from <span className="text-sky-300">{new Date(confirmDelete.created_at).toLocaleString()}</span>? This cannot be undone.</p>
          <div className="mt-5 flex gap-2 justify-end">
            <button data-testid="delete-backup-cancel" onClick={() => setConfirmDelete(null)} disabled={busy} className="px-4 py-2 text-sm border border-zinc-700 text-zinc-300 hover:border-zinc-500 rounded-sm">Cancel</button>
            <button data-testid="delete-backup-confirm-btn" onClick={doDelete} disabled={busy} className="px-4 py-2 text-sm font-cinzel uppercase tracking-wide bg-red-600 text-white hover:bg-red-500 rounded-sm disabled:opacity-50">{busy ? "Deleting…" : "Delete"}</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
